import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import SkudPassEditModal from '@/components/SkudPassEditModal';
import { useCan } from '@/auth/AuthContext';

/**
 * Вкладка «СКУД» в карточке ученика. Данные приходят одним запросом
 * GET /api/students/{id}/skud-overview — по ученику и каждому контакту:
 *
 *   • linked — явная привязка (students.skud_person_id, для контактов —
 *     через учётку: contacts.user_id → users.skud_person_id). Полная
 *     карточка с управлением пропусками.
 *   • matches — люди из кэша СКУД, совпавшие по ФИО, но не привязанные.
 *     Показываем той же карточкой с бейджем «по совпадению ФИО».
 *
 * Секция «Ученик» есть всегда — когда ученикам начнём выдавать пропуска,
 * они появятся именно здесь (после привязки в форме редактирования).
 */
export default function SkudTab({ student }) {
  const [people, setPeople]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true); setError(null);
    api.get(`/api/students/${student.id}/skud-overview`)
      .then((res) => { if (!aborted) setPeople(res.people || []); })
      .catch((e) => { if (!aborted) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [student.id, student.skudPerson?.skudId]);

  if (loading) return <div style={{ padding: 16, color: 'var(--fg-muted)' }}>Загрузка СКУД…</div>;
  if (error)   return <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>;

  const studentEntry   = (people || []).find((p) => p.kind === 'student');
  const contactEntries = (people || []).filter((p) => p.kind === 'contact');

  const sectionTitle = (text) => (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--fg-muted)', margin: '10px 2px 8px',
    }}>
      {text}
    </div>
  );

  return (
    <div style={{ padding: '8px 12px' }}>
      {sectionTitle('Ученик')}
      {studentEntry && renderEntry(studentEntry)}

      {sectionTitle('Контактные лица')}
      {contactEntries.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', padding: '2px 2px 8px' }}>
          У ученика нет контактных лиц.
        </div>
      ) : contactEntries.map((p) => renderEntry(p))}
    </div>
  );
}

/** Одна запись overview: привязанная карточка, карточки-совпадения или заглушка. */
function renderEntry(p) {
  if (p.linked) {
    return (
      <PersonCard key={p.kind + ':' + p.id} person={{ ...p, skudPerson: p.linked }} />
    );
  }
  if (p.matches && p.matches.length > 0) {
    return (
      <React.Fragment key={p.kind + ':' + p.id}>
        {p.matches.map((m) => (
          <PersonCard
            key={p.kind + ':' + p.id + ':m' + m.skudId}
            person={{ ...p, skudPerson: m }}
            matchOnly
          />
        ))}
      </React.Fragment>
    );
  }
  return (
    <div key={p.kind + ':' + p.id} style={{
      border: '1px dashed var(--border)', borderRadius: 8, padding: '10px 12px',
      marginBottom: 12, fontSize: 12.5, color: 'var(--fg-muted)',
    }}>
      <span style={{ color: 'var(--fg)', fontWeight: 500 }}>{p.name}</span>
      <span style={{ marginLeft: 8 }}>({p.role})</span>
      {' — '}записи в СКУД нет: ни привязки, ни совпадения по ФИО.
      {p.kind === 'student' && (
        <div style={{ marginTop: 4 }}>
          Когда ученику выдадут пропуск, он появится здесь. Привязать к записи СКУД можно
          в форме редактирования ученика — поле «Человек в СКУД».
        </div>
      )}
    </div>
  );
}

function PersonCard({ person, matchOnly = false }) {
  const sp = person.skudPerson;
  const can = useCan();
  const canWrite  = can('skud.passes', 'write');
  const canDelete = can('skud.passes', 'delete');

  const [passes, setPasses] = useState(null);
  const [loadingPasses, setLoadingPasses] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPass, setEditPass]     = useState(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);

  const reload = () => {
    setLoadingPasses(true);
    return api.get(`/api/skud/persons/${sp.skudId}/passes`)
      .then((res) => setPasses(res.rows || []))
      .catch(() => setPasses([]))
      .finally(() => setLoadingPasses(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sp.skudId]);

  const fio = sp.lastName || sp.firstName
    ? [sp.lastName, sp.firstName, sp.middleName].filter(Boolean).join(' ')
    : person.name;

  const toggleBlocked = async (p) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await api.put(`/api/skud/passes/${p.skudId}`, { isBlocked: !p.isBlocked });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось изменить статус');
    } finally { setBusy(false); }
  };

  const onDelete = async (p) => {
    if (busy) return;
    if (!confirm(`Удалить пропуск ${p.code}? После подтверждения изменение уйдёт в Orion.`)) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/api/skud/passes/${p.skudId}`);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось удалить');
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
      background: 'var(--panel)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {sp.photoThumb ? (
          <img src={sp.photoThumb} alt=""
               style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--panel-2, #f0f0f0)',
            display: 'grid', placeItems: 'center',
            color: 'var(--fg-faint)',
          }}>
            <Icon name="user" size={26} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{fio}</div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {person.role}
            {matchOnly && (
              <span
                className="pay-st pay-st--mute"
                style={{ marginLeft: 8, fontSize: 10.5 }}
                title="Запись найдена в СКУД по совпадению ФИО, но не привязана. Для надёжной связи откройте редактирование ученика или контакта и выберите её в поле «Человек в СКУД»."
              >
                по совпадению ФИО
              </span>
            )}
            {sp.isDismissed && <span style={{ marginLeft: 8, color: 'var(--red-fg)' }}>уволен в СКУД</span>}
            {sp.department && <span style={{ marginLeft: 8 }}>{sp.department}</span>}
            <span style={{ marginLeft: 8 }} className="mono">СКУД #{sp.skudId}</span>
          </div>
        </div>
        {canWrite && (
          <button type="button" className="btn btn--primary btn--sm"
                  onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={12} /> Пропуск
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: 'var(--red-fg)', fontSize: 12.5, marginTop: 8 }}>{error}</div>
      )}

      <div style={{ marginTop: 10 }}>
        {loadingPasses ? (
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Загружаем пропуска…</div>
        ) : passes.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Пропусков нет.</div>
        ) : (
          <PassesTable
            rows={passes}
            canWrite={canWrite}
            canDelete={canDelete}
            busy={busy}
            onEdit={(p) => setEditPass(p)}
            onToggleBlocked={toggleBlocked}
            onDelete={onDelete}
          />
        )}
      </div>

      {createOpen && (
        <SkudPassEditModal
          mode="create"
          skudPersonId={sp.skudId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); reload(); }}
        />
      )}
      {editPass && (
        <SkudPassEditModal
          mode="edit"
          pass={editPass}
          onClose={() => setEditPass(null)}
          onSaved={() => { setEditPass(null); reload(); }}
        />
      )}
    </div>
  );
}

function PassesTable({ rows, canWrite, canDelete, busy, onEdit, onToggleBlocked, onDelete }) {
  const fmt = (s) => {
    if (!s) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
  };
  const hasActions = canWrite || canDelete;
  return (
    <table className="table" style={{ fontSize: 12.5 }}>
      <thead>
        <tr>
          <th style={{ width: 90 }}>СКУД #</th>
          <th>Код карты</th>
          <th style={{ width: 110 }}>С</th>
          <th style={{ width: 110 }}>По</th>
          <th style={{ width: 130 }}>Статус</th>
          <th>Комментарий</th>
          {hasActions && <th style={{ width: 160, textAlign: 'right' }}>Действия</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.skudId}>
            <td className="mono">{r.skudId > 0 ? r.skudId : <span className="muted">локальный</span>}</td>
            <td className="mono">{r.code}</td>
            <td className="mono">{fmt(r.startDate)}</td>
            <td className="mono">{fmt(r.endDate)}</td>
            <td>
              {r.isBlocked ? (
                <span style={{ color: 'var(--red-fg)' }}>✗ заблокирован</span>
              ) : (
                <span style={{ color: 'var(--green-700)' }}>✓ активен</span>
              )}
              {r.pendingSync && (
                <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10 }}
                      title="Изменения ещё не отправлены в Orion">
                  в очереди
                </span>
              )}
            </td>
            <td style={{ color: 'var(--fg-muted)' }}>{r.comment || '—'}</td>
            {hasActions && (
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {canWrite && (
                  <button type="button" className="btn btn--sm" disabled={busy}
                          onClick={() => onEdit(r)}
                          style={{ marginRight: 4 }}
                          title="Изменить срок / группу / комментарий">
                    <Icon name="edit" size={11} />
                  </button>
                )}
                {canWrite && (
                  <button type="button" className="btn btn--sm" disabled={busy}
                          onClick={() => onToggleBlocked(r)}
                          style={{ marginRight: 4 }}
                          title={r.isBlocked ? 'Разблокировать' : 'Заблокировать'}>
                    {r.isBlocked ? '🔓' : '🔒'}
                  </button>
                )}
                {canDelete && (
                  <button type="button" className="btn btn--sm" disabled={busy}
                          onClick={() => onDelete(r)}
                          title="Удалить пропуск"
                          style={{ color: 'var(--red-fg, #dc2626)' }}>
                    <Icon name="trash" size={11} />
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
